<template>
  <div class="service-detail-root">
    <ServicesServiceHero
      title="API Design & Development"
      description="Building robust, scalable, and secure interfaces for your applications"
      icon="✨"
      gradient="api-development"
    />
    <!-- Main Content -->
    <div class="container" style="max-width: 1000px;">
      <div class="row">
        <div class="col-md-12">
          <div class="service-main-card">
            <h2 style="color: #1a202c; font-weight: 700; margin-bottom: 24px; font-size: 2.2rem;">Connect Your Digital Ecosystem 🌐</h2>
            <p style="color: #4a5568; line-height: 1.7; font-size: 1.1rem; margin-bottom: 40px;">In today's interconnected world, APIs (Application Programming Interfaces) are the foundation of digital transformation. They enable seamless communication between applications, services, and systems. Our API design and development services help you create powerful, secure, and scalable interfaces that drive business value and innovation.</p>
            <h3 style="color: #1a202c; font-weight: 600; margin-bottom: 30px; font-size: 1.8rem;">API Design Principles</h3>
            <div class="row">
              <div class="col-md-6 mb-4">
                <div style="background: #f7fafc; padding: 24px; border-radius: 12px; border-left: 4px solid var(--api-development-start); height: 100%;">
                  <h4 style="color: #1a202c; font-weight: 600; margin-bottom: 12px;">🔍 RESTful Design</h4>
                  <p style="color: #4a5568; margin: 0; line-height: 1.6;">Creating intuitive, resource-oriented APIs with consistent patterns and best practices</p>
                </div>
              </div>
              <div class="col-md-6 mb-4">
                <div style="background: #f7fafc; padding: 24px; border-radius: 12px; border-left: 4px solid var(--api-development-start); height: 100%;">
                  <h4 style="color: #1a202c; font-weight: 600; margin-bottom: 12px;">🛡️ Security First</h4>
                  <p style="color: #4a5568; margin: 0; line-height: 1.6;">Implementing robust authentication, authorization, and data protection measures</p>
                </div>
              </div>
              <div class="col-md-6 mb-4">
                <div style="background: #f7fafc; padding: 24px; border-radius: 12px; border-left: 4px solid var(--api-development-start); height: 100%;">
                  <h4 style="color: #1a202c; font-weight: 600; margin-bottom: 12px;">⚡ Performance</h4>
                  <p style="color: #4a5568; margin: 0; line-height: 1.6;">Optimizing for speed, efficiency, and minimal latency</p>
                </div>
              </div>
              <div class="col-md-6 mb-4">
                <div style="background: #f7fafc; padding: 24px; border-radius: 12px; border-left: 4px solid var(--api-development-start); height: 100%;">
                  <h4 style="color: #1a202c; font-weight: 600; margin-bottom: 12px;">📈 Scalability</h4>
                  <p style="color: #4a5568; margin: 0; line-height: 1.6;">Building APIs that grow with your business without performance degradation</p>
                </div>
              </div>
            </div>
            <h3 style="color: #1a202c; font-weight: 600; margin-bottom: 30px; margin-top: 50px; font-size: 1.8rem;">API Development Approaches</h3>
            <div class="row mb-5">
              <div class="col-md-4 mb-4">
                <div style="background: linear-gradient(135deg, var(--api-development-start) 0%, var(--api-development-end) 100%); color: white; padding: 32px; border-radius: 16px; height: 100%;">
                  <h4 style="font-weight: 700; margin-bottom: 16px; font-size: 1.3rem;">🌐 RESTful APIs</h4>
                  <p style="margin-bottom: 16px; line-height: 1.6;">Resource-oriented interfaces using standard HTTP methods</p>
                  <ul style="margin: 0; padding-left: 20px; line-height: 1.8;">
                    <li>HTTP/HTTPS protocols</li>
                    <li>JSON/XML data formats</li>
                    <li>Stateless architecture</li>
                    <li>Ideal for most web applications</li>
                  </ul>
                </div>
              </div>
              <div class="col-md-4 mb-4">
                <div style="background: linear-gradient(135deg, var(--api-development-start) 0%, var(--api-development-end) 100%); color: white; padding: 32px; border-radius: 16px; height: 100%;">
                  <h4 style="font-weight: 700; margin-bottom: 16px; font-size: 1.3rem;">⚡ GraphQL</h4>
                  <p style="margin-bottom: 16px; line-height: 1.6;">Query language for APIs with precise data retrieval</p>
                  <ul style="margin: 0; padding-left: 20px; line-height: 1.8;">
                    <li>Client-specified queries</li>
                    <li>Single endpoint architecture</li>
                    <li>Strongly typed schema</li>
                    <li>Perfect for complex data needs</li>
                  </ul>
                </div>
              </div>
              <div class="col-md-4 mb-4">
                <div style="background: linear-gradient(135deg, var(--api-development-start) 0%, var(--api-development-end) 100%); color: white; padding: 32px; border-radius: 16px; height: 100%;">
                  <h4 style="font-weight: 700; margin-bottom: 16px; font-size: 1.3rem;">🔄 gRPC</h4>
                  <p style="margin-bottom: 16px; line-height: 1.6;">High-performance RPC framework</p>
                  <ul style="margin: 0; padding-left: 20px; line-height: 1.8;">
                    <li>Protocol Buffers</li>
                    <li>HTTP/2 based transport</li>
                    <li>Bi-directional streaming</li>
                    <li>Ideal for microservices</li>
                  </ul>
                </div>
              </div>
            </div>
            <h3 style="color: #1a202c; font-weight: 600; margin-bottom: 30px; margin-top: 50px; font-size: 1.8rem;">API Management & Documentation</h3>
            <p style="margin-top: 30px; margin-bottom: 0; font-style: italic; opacity: 0.9;">Your APIs are the foundation of your digital ecosystem.</p>
            <div class="row mb-5">
              <div class="col-md-12">
                <div style="background: #f7fafc; padding: 32px; border-radius: 16px; height: 100%;">
                  <h4 style="color: #1a202c; font-weight: 600; margin-bottom: 16px;">📚 Documentation</h4>
                  <p style="color: #4a5568; margin-bottom: 16px; line-height: 1.6;">Comprehensive API documentation is crucial for adoption and usability.</p>
                  <ul style="color: #4a5568; padding-left: 20px; line-height: 1.7;">
                    <li><strong>OpenAPI/Swagger</strong> - Industry standard for RESTful API documentation</li>
                    <li><strong>Interactive Documentation</strong> - Try-it-now functionality for developers</li>
                    <li><strong>Code Examples</strong> - Sample implementations in multiple languages</li>
                    <li><strong>Versioning</strong> - Clear documentation of API changes and deprecations</li>
                  </ul>
                </div>
              </div>
            </div>
            <!-- Pricing Section -->
            <div style="background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%); padding: 40px; border-radius: 16px; margin-top: 50px; border-left: 4px solid #3b82f6; text-align: center;">
              <h3 style="color: #1a202c; font-weight: 700; margin-bottom: 16px; font-size: 1.8rem;">Transparent Pricing</h3>
              <p style="color: #4a5568; font-size: 1.1rem; line-height: 1.7; margin-bottom: 24px; max-width: 700px; margin-left: auto; margin-right: auto;">
                Standard rate of <strong style="color: #1a202c; font-size: 1.2rem;">$225/hour</strong> for consulting and implementation. Volume discounts and retainer packages available.
              </p>
              <NuxtLink to="/services/pricing" style="display: inline-block; background: #3b82f6; color: white; padding: 14px 32px; border-radius: 8px; font-weight: 600; text-decoration: none; font-size: 1.05rem; transition: background 0.2s;">
                View Full Pricing Details
              </NuxtLink>
            </div>

            <!-- CTA Section -->
            <div style="background: linear-gradient(135deg, var(--api-development-start) 0%, var(--api-development-end) 100%); color: white; padding: 40px; border-radius: 16px; margin-top: 50px; text-align: center;">
              <h3 style="font-weight: 700; margin-bottom: 20px; font-size: 1.8rem;">Let's Build Exceptional APIs Together</h3>
              <p style="font-size: 1.1rem; opacity: 0.95; line-height: 1.6; margin-bottom: 30px;">Whether you need to integrate with partners, power your mobile apps, or enable digital transformation, our API experts are ready to help you succeed.</p>
              <div style="display: flex; gap: 20px; justify-content: center; flex-wrap: wrap;">
                <div style="background: rgba(255,255,255,0.2); padding: 20px; border-radius: 12px; backdrop-filter: blur(10px); border: 1px solid rgba(255,255,255,0.3); flex: 1; min-width: 200px; max-width: 250px;">
                  <h4 style="font-weight: 600; margin-bottom: 12px;">🔗 Integrations</h4>
                  <p style="margin: 0; font-size: 0.95rem; opacity: 0.9;">Seamless connectivity with third-party platforms</p>
                </div>
                <div style="background: rgba(255,255,255,0.2); padding: 20px; border-radius: 12px; backdrop-filter: blur(10px); border: 1px solid rgba(255,255,255,0.3); flex: 1; min-width: 200px; max-width: 250px;">
                  <h4 style="font-weight: 600; margin-bottom: 12px;">⚡ Performance</h4>
                  <p style="margin: 0; font-size: 0.95rem; opacity: 0.9;">Fast, reliable, and scalable API solutions</p>
                </div>
                <div style="background: rgba(255,255,255,0.2); padding: 20px; border-radius: 12px; backdrop-filter: blur(10px); border: 1px solid rgba(255,255,255,0.3); flex: 1; min-width: 200px; max-width: 250px;">
                  <h4 style="font-weight: 600; margin-bottom: 12px;">🛡️ Security</h4>
                  <p style="margin: 0; font-size: 0.95rem; opacity: 0.9;">Best-in-class protection for your data and users</p>
                </div>
              </div>
              <p style="margin-top: 30px; margin-bottom: 0; font-style: italic; opacity: 0.9;">From strategy to implementation, we're your API partner every step of the way.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import '@/assets/css/service-detail-card.css'
useHead({
  title: 'API Design & Development | Geek Cafe',
  meta: [
    { name: 'description', content: 'Building robust, scalable, and secure interfaces for your applications. We specialize in RESTful APIs, GraphQL, and gRPC to connect your digital ecosystem.' }
  ]
})
</script>
